import { useState } from "react";

import { Button, Grid } from "@material-ui/core";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ServerlessDeleteButton from "./ServerlessDeleteButton";

import { useCheckLists } from "../../utilHooks/checkLists";

import Switch from "./Switch";

export type Charactor = {
  checklist_id: string | undefined;
  item_name: string | undefined;
};

export type CheckList = {
  checklist_group_id: string;
  checklist_memo: string;
  checklist_name: string;
  company_id: string;
  created_at: string;
  created_by: string;
  list: Charactor[];
  updated_at: string;
  updated_by: string;
};

export type ChooseCheckListProps = {
  onSwitch: ({ checklistActivation }: { checklistActivation: string }) => void;
  onSelect: ({ checkItems }: { checkItems: any }) => void;
  checkList: any[];
  checklistActivation: string;
};

export default function ChooseCheckList(props: ChooseCheckListProps) {
  const propCheckList: Charactor[] = props.checkList?.map((item) => ({
    checklist_id: item.checklist_id,
    item_name: item.checklist_name,
  }));

  const { checkLists, mutateCheckLists, error } = useCheckLists();

  const [isUse, setIsUse] = useState<boolean>(
    props.checklistActivation === "1"
  );

  const [choosedList, setChoosedList] = useState<Charactor[]>([]);

  const [selectedCheckList, setSelectedCheckList] = useState<CheckList>();
  const [selected, setSelected] = useState<string>();

  const [validErrorList, setValidErrorList] = useState<string[][]>([]);

  const [isSettingMode, setIsSettingMode] = useState<boolean>(
    props.checklistActivation === "1"
  );

  if (!checkLists) {
    return <Loading />;
  }

  if (isSettingMode) {
    /*** 変更ボタン押下時 ***/
    const onClickChangeButton = () => {
      setIsSettingMode(false);
      setIsUse(true);
      if (propCheckList.length > 0) {
        setChoosedList(propCheckList);
      }
    };

    return (
      <FormGroup>
        <Stack spacing={1} marginTop={1}>
          <Grid container spacing={0} justifyContent="flex-start">
            <Grid item xs={2}>
              <Button
                style={{ color: "#fff", backgroundColor: "#FFA500" }}
                variant="contained"
                size="small"
                onClick={onClickChangeButton}
              >
                変更
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" gutterBottom component="div">
                設定済
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </FormGroup>
    );
  }

  /*** チェックリストセレクトボックス変更時 ***/
  const onChangeCheckList = (e: any) => {
    const checkList: CheckList = checkLists?.find(
      (checkList: any) => checkList.checklist_group_id === e.target.value
    );
    setChoosedList(checkList?.list);
    setSelectedCheckList(checkList);
    setSelected(e.target.value);
  };

  /*** チェックリスト追加ボタン押下時 ***/
  const onClickAddCheckListButton = () => {
    if (!selectedCheckList) {
      window.alert("チェックリストを選択してください。");
      return;
    }

    if (choosedList.length >= 15) {
      window.alert("チェック項目は15個以上追加出来ません。");
      return;
    }
    setChoosedList((choosedList) => [
      ...choosedList,
      { checklist_id: choosedList.length + 1 + "", item_name: "" },
    ]);
  };

  /*** チェックリスト名テキスト入力時 ***/
  const onChangeCheckText = (selected: number) => (e: any) => {
    setChoosedList(
      choosedList.map((checkList: Charactor, index: number) => {
        if (index === selected) {
          return { ...checkList, item_name: e.target.value };
        }
        return checkList;
      })
    );
  };

  /*** 選択ボタン押下時 ***/
  const onClickSelectButton = async () => {
    const validErrors: string[][] = choosedList.map((checkItem: Charactor) => {
      const messages: string[] = [];

      if (!checkItem.item_name) {
        messages.push("必須項目です。");
      }

      else if (checkItem.item_name.length >= 50) {
        messages.push("50文字以下で入力してください。");
      }
      
      return messages;
    });
    setValidErrorList(validErrors);

    if (validErrors.filter((err) => err.length >= 1).length >= 1) {
      return;
    }

    if (choosedList.length === 0) {
      alert("チェック項目が0個です。最低1つは登録してください。");
      return;
    }

    props.onSelect({
      checkItems: {
        ...choosedList.map((item) => ({
          checklist_name: item.item_name,
          check: "0",
        })),
      },
    });
    setIsSettingMode(true);
  };

  /*** 削除ボタン押下時 ***/
  const onClickDeleteButton = (selected: number) => () => {
    setValidErrorList([]);
    setChoosedList(
      choosedList.filter((checkList: Charactor, index: number) => {
        return index !== selected;
      })
    );
  };

  /*** キャンセルボタン押下時 ***/
  const onClickCancelButton = (e: any) => {
    clearAll();
  };

  /*** スイッチボタン押下時 ***/
  const onClickSwitch = (e: any) => {
    setChoosedList(propCheckList);
    setIsUse(e.target.checked);
    props.onSwitch({ checklistActivation: e.target.checked ? "1" : "0" });
  };

  const clearAll = () => {
    setSelected("empty");
    setChoosedList(propCheckList);
    setValidErrorList([]);
    mutateCheckLists();
  };

  return (
    <FormGroup>
      <Switch
        label="チェックリストを使用する"
        onChange={onClickSwitch}
        checked={isUse}
      />
      <br />
      <TextField
        id="outlined-select-currency-native"
        select
        label="チェックリスト"
        SelectProps={{
          native: true,
        }}
        disabled={!isUse}
        className="InsText"
        variant="outlined"
        onChange={onChangeCheckList}
        value={selected}
      >
        <option key="empty" value="empty"></option>
        {checkLists?.map((checkList: any, index: number) => (
          <option key={index} value={checkList.checklist_group_id}>
            {checkList.checklist_name}
          </option>
        ))}
      </TextField>

      {isUse && (
        <Stack spacing={0} marginTop={1}>
          <Grid container spacing={0} justifyContent="flex-start">
            <Grid item xs={9}>
              <Button
                color="primary"
                variant="contained"
                size="medium"
                onClick={onClickAddCheckListButton}
                disabled={!isUse}
              >
                ＋　チェック項目追加
              </Button>
            </Grid>
          </Grid>
        </Stack>
      )}

      {isUse && (
        <>
          <Stack spacing={0} marginTop={1}>
            <Grid container spacing={0} justifyContent="flex-start">
              <div
                className="textBoxCheck_CheckList"
                style={{
                  padding: "30px",
                  overflowY: "scroll",
                  height: "300px",
                }}
              >
                <section>
                  <table>
                    <tbody>
                      {choosedList?.map((check: Charactor, index: number) => (
                        <tr key={index}>
                          <td style={{ width: "10%", fontSize: "19px" }}>
                            {index + 1}
                          </td>
                          <td style={{ width: "70%" }}>
                            <TextField
                              id="outlined-basic"
                              variant="outlined"
                              className="InsText"
                              multiline={true}
                              value={check.item_name}
                              onChange={onChangeCheckText(index)}
                              size="small"
                              disabled={!isUse}
                            />
                            <Typography
                              variant="subtitle1"
                              color="red"
                              gutterBottom
                              component="div"
                            >
                              {validErrorList[index]?.map((error) => (
                                <div>{error}</div>
                              ))}
                            </Typography>
                          </td>

                          <td style={{ width: "100px" }}>
                            <ServerlessDeleteButton
                              onClick={onClickDeleteButton(index)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </div>
            </Grid>
          </Stack>

          <Stack spacing={0} marginTop={1}>
            <Grid container spacing={0} justifyContent="flex-end">
              <Grid item xs={2}>
                <Button
                  color="primary"
                  variant="contained"
                  size="medium"
                  disabled={!isUse}
                  onClick={onClickSelectButton}
                >
                  決定
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button
                  disabled={!isUse}
                  color="inherit"
                  variant="contained"
                  size="medium"
                  onClick={onClickCancelButton}
                >
                  キャンセル
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </>
      )}
    </FormGroup>
  );
}

function Loading() {
  return (
    <Box sx={{ display: "flex" }}>
      <CircularProgress />
    </Box>
  );
}
